import React from "react";
import { connect, ConnectedProps } from "react-redux";
import * as actions from "../../actionCreator";
import { GlobalApplicationState } from "globalApplicationState";
import { push } from "react-router-redux";
import moment from "moment";

import LoadingOverlay from "modules/common/components/loadingOverlay";

import { 
    Capacity as ICapacity, 
    Event, 
    EventTimes as IEventTimes, 
    EventType as IEventType, 
    Notifications as INotifications, 
    Questions as IQuestions, 
    Tag, 
    TranslatedContent as ITranslatedContent, 
    ValidationChecks 
} from "../../models";

import Author from "modules/common/components/authoring/author";
import ExpiryTime from "modules/common/components/authoring/expiryTime";
import FileAttachments from "modules/common/components/authoring/fileAttachments";
import HeroBanner from "modules/common/components/authoring/heroBanner/heroBanner";
import Notifications from "modules/common/components/authoring/notifications";
import PublishTime from "modules/common/components/authoring/publishTime";
import Reminders from "modules/common/components/authoring/reminders";
import AuthoringWarnings from "modules/common/components/authoring/authoringWarnings";
import TranslatedContent from "modules/common/components/authoring/translatedContent";
import Validator from "modules/common/components/authoring/validator";

import UpdateInPersonCapacityConfirmation from "../confirmations/updateInPersonCapacity";
import UpdateOnlineCapacityConfirmation from "../confirmations/updateOnlineCapacity";

import Capacity from "./components/capacity";
import CutoffTime from "./components/cutoffTime";
import EventTimes from "./components/eventTimes";
import EventType from "./components/eventType";
import Location from "./components/location";
import Questions from "./components/questions";

import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import { AttachedFile } from "modules/gallery";
import { CroppableImage } from "modules/common/components/authoring/models";
import { 
    getDefaultNotificationSetting,
    isAnyCustomNotificationOptionOn,
    isAnyTenantEventNotificationsEnabled,
    isAnyTenantEventRemindersEnabled
} from "utils/notificationsHelper";
import { NotificationEvents } from "modules/messaging";
import { ICustomCssModelv1 } from "api/files";
import { mentionCheck } from "utils/regexUserValidation";
import { EventCommandBar, EventCommandBarAction, IEventCommandBarAction } from "./components/eventCommandBar";
import { MultiSelectTopic, MultiSelectTopicGroup, TenantSettingsTag, TenantSettingsTagGroup } from "modules/settings";
import MultiselectAndChipsWithSearch from "modules/common/components/forms/inputs/multiSelectAndChipsWithSearch";
import { useDefaultLocale } from "modules/common/hooks/useDefaultLocale";
import { ActionButtonInput } from "modules/common/components/actionButtonInput/actionButtonInput";
import { defaultActionButtons } from "modules/common/models";

const EventEditor: React.FunctionComponent<IConnectedEventEditorProps> = ({
    notificationSettings,
    event,
    currentUser,
    customCss,
    validationChecks,
    showNotificationSettings,
    showReminderSettings,
    isFetching,
    topicGroups,
    getContentAnalysis,
    getDraftTranslation,
    onShowNotificationSettings,
    onShowReminderSettings,
    onChangeEvent,
}) => {
    const getSelectedTopicDetails = React.useCallback((selectedTopics?: Tag[]): MultiSelectTopic[] => {
        return topicGroups
            .flatMap((group: TenantSettingsTagGroup) => group.tags)
            .filter((topic: TenantSettingsTag) => (selectedTopics?.map((postTag: Tag) => postTag.id) || []).includes(topic.id))
            .map((topic) => ({...topic}))
            ?? [];
    }, [topicGroups]);

    const getEventCommandBarActions = () : Record<EventCommandBarAction, IEventCommandBarAction> => {
        return {
            [EventCommandBarAction.AddOrganizer] : { onClick: () => setShowAuthor(true), disabled: showAuthor },
            [EventCommandBarAction.AddQuestion] : { onClick: () => setShowQuestions(true), disabled: showQuestions },
            [EventCommandBarAction.AttachFiles] : { onClick: () => setShowAttachedFiles(true), disabled: showAttachedFiles },
            [EventCommandBarAction.ShowBanner] : { onClick: () => setShowHeroBanner(true), disabled: showHeroBanner },
            [EventCommandBarAction.ShowSummary] : { onClick: () => setShowSummary(true), disabled: showSummary },
        };
    }

    const eventHasBannerImage = (event: Partial<Event>) => !!event.image?.id || !!event.attachedContent?.length;

    const [hasNotifications, setHasNotifications] = React.useState<boolean>(isAnyCustomNotificationOptionOn(event.notifications));
    const [hasReminders, setHasReminders] = React.useState<boolean>(!!event.notifications && !!event.notifications.reminders && !!event.notifications.reminders.length);
    const [showAttachedFiles, setShowAttachedFiles] = React.useState<boolean>(!!event.fileAttachments && !!event.fileAttachments.length);
    const [showAuthor, setShowAuthor] = React.useState<boolean>(true);
    const [showHeroBanner, setShowHeroBanner] = React.useState<boolean>(eventHasBannerImage(event));
    const [showMoreOptions, setShowMoreOptions] = React.useState<boolean>(false);
    const [showQuestions, setShowQuestions] = React.useState<boolean>(!!event.questions && !!Object.keys(event.questions).length);
    const [showUpdateInPersonCapacityConfirmation, setShowUpdateInPersonCapacityConfirmation] = React.useState<boolean>(false);
    const [showSummary, setShowSummary] = React.useState<boolean>(true);
    const [showUpdateOnlineCapacityConfirmation, setShowUpdateOnlineCapacityConfirmation] = React.useState<boolean>(false);
    const [hasMention, setHasMention] = React.useState<boolean>(false);
    const [selectedTopics, setSelectedTopics] = React.useState<MultiSelectTopic[]>(getSelectedTopicDetails(event.tags));
    useDefaultLocale();

    React.useEffect(() => {
        if (event.tags?.length) {
            setSelectedTopics(getSelectedTopicDetails(event.tags));
        }
    }, [event.tags, setSelectedTopics, getSelectedTopicDetails]);

    React.useEffect(() => {
        if (eventHasBannerImage(event)) {
            setShowHeroBanner(true);
        }
    }, [event.image, event.attachedContent]);

    const hasSetPublishedNotificationsOff = React.useRef(false);

    // re-editing an already-published event shouldn't re-notify subscribers by default, matching Posts' behavior
    React.useEffect(() => {
        if (hasSetPublishedNotificationsOff.current) return;

        if (event.isPreviouslyPublished && event.publishTime && new Date(event.publishTime).toISOString() <= new Date().toISOString()) {
            hasSetPublishedNotificationsOff.current = true;
            onChangeEvent({
                notifications: {
                    ...event.notifications!,
                    emailOnPublish: "none",
                    mobileOnPublish: "none",
                    smsOnPublish: "none",
                    teamsOnPublish: "none"
                }
            });
            setHasNotifications(false);
        }
    }, [event.isPreviouslyPublished, event.publishTime, event.notifications, onChangeEvent]);

    /**
     * Remove disabled topics and empty topic groups before rendering
     */
    const filterTopicGroups = () : MultiSelectTopicGroup[] => {
        return topicGroups.map((group: TenantSettingsTagGroup) => ({
            ...group,
            children: group.tags.filter((tag: TenantSettingsTag) => !tag.disabled)
        })).filter((group: TenantSettingsTagGroup) => group.tags.length > 0);
    }

    const onChangeAttachedContent = (attachedContent: AttachedFile[]) => {
        onChangeEvent({ attachedContent });
    }

    const onChangeAuthor = (author: string) => {
        onChangeEvent({ author });
    }

    const onChangeAuthorEmail = (authorEmail: string) => {
        onChangeEvent({ authorEmail });
    }

    const onChangeCapacity = (capacity: ICapacity) => {
        onChangeEvent({ capacity });
    }

    const onChangeCutoffTime = (cutoffTime: string) => {
        onChangeEvent({ cutoffTime });
    }

    const onChangeEventTimes = (eventTimes: IEventTimes) => {
        onChangeEvent({ eventTimes });
    }

    const onChangeExpiryTime = (expiryTime: string) => {
        onChangeEvent({ expiryTime });
    }

    const onChangeFileAttachments = (fileAttachments: AttachedFile[]) => {
        onChangeEvent({ fileAttachments });
    }

    const onChangeImage = (image: CroppableImage) => {
        onChangeEvent({ image });
    }

    const onChangeEventType = (eventType: IEventType) => {
        if (eventType === "informational") {
            onChangeEvent({
                capacity: { InPerson: -1, Online: -1, Waitlist: -1, NotAttending: -1 },
                cutoffTime: undefined,
                eventType,
                questions: undefined,
                respondingEnabled: false
            });
            setShowQuestions(false);
        } else {
            const eventStartTime: string = Object.keys(event.eventTimes!)[0];
            const cutoffTime: string = moment(eventStartTime).startOf("day").toISOString();
            onChangeEvent({ eventType, cutoffTime, respondingEnabled: true });
        }
    }

    const onChangeLocation = (location: string) => {
        const showConfirmation: boolean = !!event.location && !location && event.eventType !== "informational" && !!event.capacity && event.capacity.InPerson !== 0;
        onChangeEvent({ location });
        if (showConfirmation)
            setShowUpdateInPersonCapacityConfirmation(true);
    }

    const onChangeLocationLink = (locationLink: string) => {
        const showConfirmation: boolean = !!event.locationLink && !locationLink && event.eventType !== "informational" && !!event.capacity && event.capacity.Online !== 0;
        onChangeEvent({ locationLink });
        if (showConfirmation)
            setShowUpdateOnlineCapacityConfirmation(true);
    }

    const onChangeLocationLinkLabel = (locationLinkLabel: string) => {
        const showConfirmation: boolean = !!event.locationLinkLabel && !locationLinkLabel && event.eventType !== "informational" && !!event.capacity && event.capacity.Online !== 0;
        onChangeEvent({ locationLinkLabel });
        if (showConfirmation)
            setShowUpdateOnlineCapacityConfirmation(true);
    }

    const clearLocationLink = () => {
        onChangeEvent({ locationLinkLabel: "", locationLink: "" });
    }

    const onRestoreLocationLink = (locationLink: string, locationLinkLabel: string) => {
        onChangeEvent({ locationLink, locationLinkLabel });
    }

    const onChangeNotifications = (notifications: INotifications) => {
        onChangeEvent({ notifications });

        setHasNotifications(!(notifications.emailOnPublish === "none" && notifications.mobileOnPublish === "none" && notifications.smsOnPublish === "none" && notifications.teamsOnPublish === "none"));
        setHasReminders(!!notifications.reminders && !!notifications.reminders.length);
    }

    const onChangePublishTime = (publishTime: string) => {
        onChangeEvent({ publishTime });
    }

    const onChangeQuestion = (questions: IQuestions) => {
        onChangeEvent({ questions });
    }

    const onChangeTags = (topics: Tag[]) => {
        onChangeEvent({ tags: topics });
        const newTopics = getSelectedTopicDetails(topics)
        setSelectedTopics(newTopics);
    }

    const onChangeTranslatedContent = (translatedContent: ITranslatedContent) => {
        var toTest = JSON.stringify(translatedContent);
        setHasMention(mentionCheck.test(toTest));

        onChangeEvent({ translatedContent });
    }

    const onHideUpdateInPersonCapacityConfirmation = () => {
        setShowUpdateInPersonCapacityConfirmation(false);
    }

    const onHideUpdateOnlineCapacityConfirmation = () => {
        setShowUpdateOnlineCapacityConfirmation(false);
    }

    const onRemoveAttachedFiles = () => {
        setShowAttachedFiles(false);
        onChangeEvent({ fileAttachments: [] });
    }

    const onRemoveAuthor = () => {
        setShowAuthor(false);
        onChangeEvent({ author: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : "", authorEmail: currentUser ? currentUser.email : "" });
    }

    const onRemoveHeroBanner = () => {
        setShowHeroBanner(false);
        onChangeEvent({ attachedContent: [], image: undefined });
    }

    const onRemoveImage = () => {
        onChangeEvent({ image: undefined });
    }

    const onRemoveInPersonCapacity = () => {
        onHideUpdateInPersonCapacityConfirmation();
        onChangeEvent({ capacity: { ...event.capacity!, InPerson: 0 } });
    }

    const onRemoveOnlineCapacity = () => {
        onHideUpdateOnlineCapacityConfirmation();
        onChangeEvent({ capacity: { ...event.capacity!, Online: 0 } });
    }

    const onRemoveQuestions = () => {
        setShowQuestions(false);
        onChangeEvent({ questions: undefined });
    }

    const onRemoveSummary = () => {
        setShowSummary(false);
    }

    const onSetNotifications = (ev, checked: boolean) => {
        if (!checked) {
            onChangeEvent({
                notifications: {
                    ...event.notifications!,
                    emailOnPublish: "none",
                    mobileOnPublish: "none",
                    smsOnPublish: "none",
                    teamsOnPublish: "none"
                }
            });
        } else {
            onChangeEvent({
                notifications: {
                    ...event.notifications!,
                    emailOnPublish: getDefaultNotificationSetting(NotificationEvents.EventPublishedEmail, notificationSettings),
                    mobileOnPublish: getDefaultNotificationSetting(NotificationEvents.EventPublishedMobile, notificationSettings),
                    smsOnPublish: getDefaultNotificationSetting(NotificationEvents.EventPublishedSMS, notificationSettings),
                    teamsOnPublish: getDefaultNotificationSetting(NotificationEvents.EventPublishedTeams, notificationSettings)
                }
            });
            onShowNotificationSettings(true);
        }
        setHasNotifications(checked);
    }

    // lets the notifications drawer flip the master switch off when the author manually unchecks every channel and closes it
    const onToggleNotificationsFromDrawer = (checked: boolean) => {
        onSetNotifications(undefined, checked);
    }

    const onShowLessOptions = () => {
        setShowMoreOptions(false);
    }

    const onShowMoreOptions = () => {
        setShowMoreOptions(true);
    }

    const onToggleReminderSettings = (ev, checked: boolean) => {
        if (!checked) {
            onChangeEvent({
                notifications: {
                    ...event.notifications!,
                    reminders: []
                }
            });
            setHasReminders(false);
        } else {
            onChangeEvent({
                notifications: {
                    ...event.notifications!,
                    reminders: notificationSettings.defaultEventSettings?.reminders || [{ channel: "inherit", delay: notificationSettings.defaultEventReminder || 0, range: "subscribers" }]
                }
            });
            setHasReminders(true);
            onShowReminderSettings(true);
        }
    }

    const notificationSettingsEnabled = !!event.notifications && isAnyTenantEventNotificationsEnabled(notificationSettings)
    const reminderSettingsEnabled = !!event.notifications && !!event.notifications.reminders && isAnyTenantEventRemindersEnabled(notificationSettings);
    const actionButtons = event.actionButtons ?? defaultActionButtons;

    return (
        <div>
            <EventCommandBar
                eventType={event.eventType}
                actions={getEventCommandBarActions()}
            />
            <div className="editors-container">
                <div className="main-editor">
                    <HeroBanner
                        show={showHeroBanner}
                        attachedContent={event.attachedContent}
                        image={event.image}
                        optional
                        onChangeAttachedContent={onChangeAttachedContent}
                        onChangeImage={onChangeImage}
                        onRemove={onRemoveHeroBanner}
                        onRemoveImage={onRemoveImage}
                    />
                    <Author
                        show={showAuthor}
                        author={event.author}
                        authorEmail={event.authorEmail}
                        type="Organizer"
                        onChangeAuthor={onChangeAuthor}
                        onChangeAuthorEmail={onChangeAuthorEmail}
                        onRemove={onRemoveAuthor}
                    />
                    <TranslatedContent
                        customCss={customCss}
                        showSummary={showSummary}
                        id={event.id}
                        translatedContent={event.translatedContent}
                        contentType="event"
                        getContentAnalysis={getContentAnalysis}
                        getDraftTranslation={getDraftTranslation}
                        onChange={onChangeTranslatedContent}
                        onRemoveSummary={onRemoveSummary}
                    />
                    <FileAttachments
                        show={showAttachedFiles}
                        fileAttachments={event.fileAttachments}
                        onChange={onChangeFileAttachments}
                        onRemove={onRemoveAttachedFiles}
                        itemCloseIcon={<DeleteIcon />}
                        buttonLabel="ADD FILE"
                        removeLabel="Remove All Attachments"
                        isGreyPaper
                    />
                    <Questions 
                        show={showQuestions} 
                        questions={event.questions} 
                        onChange={onChangeQuestion} 
                        onRemove={onRemoveQuestions} 
                    />
                </div>
                <div className="settings-editor">
                    <Paper elevation={0} className="section-card colored-background">
                        <Validator validationChecks={validationChecks}>
                            {event.eventType === "informational" &&
                                <div className="progress-list-info">
                                    <span className="emphasis">Event Type (Informational)</span> is set to default. Change this setting below.
                                </div>
                            }
                            {!!event.publishTime && (moment(event.publishTime) < moment()) &&
                                <div className="progress-list-info">
                                    Take note that the <span className="emphasis">Publish on</span> date is set to a past date.
                                </div>
                            }
                        </Validator>
                    </Paper>
                    <Paper elevation={0} className="section-card colored-background">
                        <EventType eventType={event.eventType} onChange={onChangeEventType} />
                        <div className="section-card-subcontent">
                            <Collapse in={event.eventType !== "informational"}>
                                <CutoffTime 
                                    cutoffTime={event.cutoffTime} 
                                    eventType={event.eventType} 
                                    disabled={!event.respondingEnabled} 
                                    onChange={onChangeCutoffTime} 
                                />
                                <Capacity 
                                    capacity={event.capacity!} 
                                    disabled={!event.respondingEnabled} 
                                    onChange={onChangeCapacity} 
                                />
                            </Collapse>
                        </div>
                    </Paper>
                    <Paper elevation={0} className="section-card colored-background">
                        <AuthoringWarnings tags={event.tags!} onChange={onChangeTags} hasMention={hasMention} />

                        <MultiselectAndChipsWithSearch
                            containerClassName="detail-input"
                            buttonText="Add/Edit Topics"
                            selectedOptions={selectedTopics}
                            onChange={onChangeTags}
                            defaultOptions={filterTopicGroups()}
                            buttonId="add-edit-topics-button"
                        />
                    </Paper>
                    <Paper elevation={0} className="section-card date-and-location colored-background">
                        <div className="section-card-heading">Event Date</div>
                        <div className="section-card-subcontent">
                            <EventTimes eventTimes={event.eventTimes} onChange={onChangeEventTimes} />
                        </div>
                        <div className="section-card-heading">Event Location</div>
                        <div className="section-card-subcontent">
                            <Location 
                                location={event.location} 
                                locationLink={event.locationLink} 
                                locationLinkLabel={event.locationLinkLabel}
                                onChangeLocation={onChangeLocation} 
                                onChangeLocationLink={onChangeLocationLink} 
                                onChangeLocationLinkLabel={onChangeLocationLinkLabel} 
                                clearLocationLink={clearLocationLink}
                                onRestoreLocationLink={onRestoreLocationLink}
                            />
                        </div>
                    </Paper>
                    <Paper elevation={0} className="section-card colored-background">
                        <div className="section-card-heading">Action Buttons</div>
                        <div className="section-card-subcontent">
                            <ActionButtonInput
                                name="Primary Button"
                                isActive={actionButtons.primaryButtonSettings.isActive}
                                buttonText={actionButtons.primaryButtonSettings.text}
                                buttonLink={actionButtons.primaryButtonSettings.link}
                                openLinkInNewWindow={actionButtons.primaryButtonSettings.openInNewWindow}
                                eventType={event.eventType}
                                setIsActive={isActive => onChangeEvent({ actionButtons: { ...actionButtons, primaryButtonSettings: { ...actionButtons.primaryButtonSettings, isActive }}})}
                                setButtonLink={link => onChangeEvent({ actionButtons: { ...actionButtons, primaryButtonSettings: { ...actionButtons.primaryButtonSettings, link }}})}
                                setOpenInNewWindow={openInNewWindow => onChangeEvent({ actionButtons: { ...actionButtons, primaryButtonSettings: { ...actionButtons.primaryButtonSettings, openInNewWindow }}})}
                                setButtonText={text => onChangeEvent({ actionButtons: { ...actionButtons, primaryButtonSettings: { ...actionButtons.primaryButtonSettings, text }}})}
                            />
                        </div>
                        <div className="section-card-subcontent secondary-action-button">
                            <ActionButtonInput
                                name="Secondary Button"
                                isActive={actionButtons.secondaryButtonSettings.isActive}
                                buttonText={actionButtons.secondaryButtonSettings.text}
                                buttonLink={actionButtons.secondaryButtonSettings.link}
                                openLinkInNewWindow={actionButtons.secondaryButtonSettings.openInNewWindow}
                                setIsActive={isActive => onChangeEvent({ actionButtons: { ...actionButtons, secondaryButtonSettings: { ...actionButtons.secondaryButtonSettings, isActive }}})}
                                setButtonLink={link => onChangeEvent({ actionButtons: { ...actionButtons, secondaryButtonSettings: { ...actionButtons.secondaryButtonSettings, link }}})}
                                setOpenInNewWindow={openInNewWindow => onChangeEvent({ actionButtons: { ...actionButtons, secondaryButtonSettings: { ...actionButtons.secondaryButtonSettings, openInNewWindow }}})}
                                setButtonText={text => onChangeEvent({ actionButtons: { ...actionButtons, secondaryButtonSettings: { ...actionButtons.secondaryButtonSettings, text }}})}
                            />
                        </div>
                    </Paper>
                    <Paper elevation={0} className="section-card colored-background">
                        {showMoreOptions
                            ? <Button variant="text" color="primary" endIcon={<ExpandLessIcon />} onClick={onShowLessOptions}>
                                Show less options
                            </Button>
                            : <Button id="options-tab" variant="text" color="primary" endIcon={<ExpandMoreIcon />} onClick={onShowMoreOptions}>
                                Show more options
                            </Button>
                        }
                    </Paper>
                    <Collapse in={showMoreOptions}>
                        {notificationSettingsEnabled &&
                            <Paper elevation={0} className="section-card colored-background">
                                <div className="section-card-heading">
                                    <div>Notify subscribers upon publishing</div>
                                    <Switch
                                        color="primary"
                                        checked={hasNotifications}
                                        onChange={onSetNotifications}
                                        id="toggle-notification-settings"
                                    />
                                </div>
                                <Collapse in={hasNotifications}>
                                    <Button 
                                        id="edit-notification-options"
                                        variant="text" 
                                        color="primary" 
                                        disableRipple 
                                        onClick={() => onShowNotificationSettings(true)} 
                                        className="notification-options"
                                    >
                                        Edit notifications
                                    </Button>
                                </Collapse>
                            </Paper>
                        }
                        {reminderSettingsEnabled &&
                            <Paper elevation={0} className="section-card colored-background">
                                <div className="section-card-heading">
                                    <div>Send reminder if event is unread</div>
                                    <Switch
                                        color="primary"
                                        checked={hasReminders}
                                        onChange={onToggleReminderSettings}
                                    />
                                </div>
                                <Collapse in={hasReminders}>
                                    <Button 
                                        variant="text" 
                                        color="primary" 
                                        disableRipple 
                                        onClick={() => onShowReminderSettings(true)}
                                        className="notification-options"
                                    >
                                        Edit reminders
                                    </Button>
                                </Collapse>
                            </Paper>
                        }
                        <Paper elevation={0} className="section-card colored-background">
                            <PublishTime publishTime={event.publishTime} onChange={onChangePublishTime} />
                            <ExpiryTime expiryTime={event.expiryTime} onChange={onChangeExpiryTime} />
                        </Paper>
                    </Collapse>
                </div>
            </div>

            <Notifications
                show={showNotificationSettings}
                isPreviouslyPublished={!!event.isPreviouslyPublished}
                notifications={event.notifications}
                contentType="event"
                onChangeNotifications={onChangeNotifications}
                onToggleNotifications={onToggleNotificationsFromDrawer}
                onClose={() => onShowNotificationSettings(false)}
            />
            <Reminders 
                show={showReminderSettings} 
                isPreviouslyPublished={!!event.isPreviouslyPublished} 
                notifications={event.notifications} 
                contentType="event" 
                onChangeNotifications={onChangeNotifications} 
                onClose={() => onShowReminderSettings(false)} 
            />
            <UpdateInPersonCapacityConfirmation 
                show={showUpdateInPersonCapacityConfirmation} 
                onConfirm={onRemoveInPersonCapacity} 
                onCancel={onHideUpdateInPersonCapacityConfirmation} 
            />
            <UpdateOnlineCapacityConfirmation show={showUpdateOnlineCapacityConfirmation} onConfirm={onRemoveOnlineCapacity} onCancel={onHideUpdateOnlineCapacityConfirmation} />
            <LoadingOverlay absolute={true} show={isFetching} />
        </div>
    );
}


interface IEventEditorProps {
    event: Partial<Event>;
    onChangeEvent: (value: Partial<Event>) => void;
    isValid: boolean;
    showNotificationSettings: boolean;
    showReminderSettings: boolean;
    validationChecks: ValidationChecks;
    onShowNotificationSettings: (value: boolean) => void;
    onShowReminderSettings: (value: boolean) => void;
    customCss?: ICustomCssModelv1;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: IEventEditorProps) => ({
        ...ownProps,
        isFetching: state.events.isFetching,
        lcidMappings: state.resources.lcidMappings,
        currentUser: state.settings.currentUser,
        documentGallery: state.events.documentGallery,
        imageGallery: state.events.imageGallery,
        notificationSettings: state.settings.notificationSettings,
        tenantSettings: state.settings.tenantSettings,
        tenant: state.tenant.id,
        topicGroups: state.settings.tenantSettings.tagGroups,
    }),
    {
        getContentAnalysis: actions.getContentAnalysis,
        getDraftTranslation: actions.getDraftTranslation,
        redirectTo: push
    }
);
type IConnectedEventEditorProps = ConnectedProps<typeof connector>;

export default connector(EventEditor);
